'use client'

import React, { useState, useCallback, useRef, useEffect } from 'react'
import { cn } from '../../utils/styles'
import { FileDropZone, FileDropZoneContent } from './components/FileDropZone'
import { FileList } from './components/FileList'
import { ValidationMessages } from './components/ValidationMessages'
import { BatchProgress } from './components/UploadProgress'
import {
  validateFiles,
  createFileWithUIState,
  generateFileId,
  cleanupPreviewUrl
} from './utils/fileUtils'
import type {
  FileUploadFieldProps,
  FileWithUIState,
  ValidationResult,
  UploadResult,
  FileUploadState
} from './types'

export function FileUploadField({
  // Hook injection (Supabase integration)
  uploadHook,
  batchUploadHook,
  validationHook,

  // Direct handlers (fallback)
  onUpload,
  onValidate,

  // Configuration
  config,

  // File management
  value = [],
  onChange,

  // Event handlers
  onFilesAdded,
  onFileRemoved,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  onValidationError,

  // UI props
  className,
  disabled = false,
  placeholder,
  children
}: FileUploadFieldProps) {
  // Internal state
  const [files, setFiles] = useState<FileWithUIState[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([])
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Refs
  const uploadAbortControllerRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialize files from value prop
  useEffect(() => {
    if (value && value.length > 0) {
      const fileStates = value.map(createFileWithUIState)
      setFiles(fileStates)
    }
  }, [value])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup any preview URLs
      files.forEach(file => {
        if (file.preview) {
          cleanupPreviewUrl(file.preview)
        }
      })

      // Cancel any ongoing uploads
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort()
      }
    }
  }, [files])

  // File validation
  const validateFilesInternal = useCallback((filesToValidate: File[]): ValidationResult[] => {
    if (validationHook) {
      return validationHook.validateFiles(filesToValidate)
    }

    if (onValidate) {
      return filesToValidate.map(onValidate)
    }

    return validateFiles(filesToValidate, config)
  }, [validationHook, onValidate, config])

  // Handle file addition
  const handleFilesAdded = useCallback((newFiles: File[]) => {
    // Validate files
    const validation = validateFilesInternal(newFiles)
    const validFiles = newFiles.filter((_, index) => validation[index].isValid)
    const invalidFiles = validation.filter(result => !result.isValid)

    // Update validation results
    setValidationResults(prev => [...prev, ...invalidFiles])

    if (invalidFiles.length > 0) {
      onValidationError?.(invalidFiles)
    }

    // Add valid files to state
    if (validFiles.length > 0) {
      const fileStates = validFiles.map(createFileWithUIState)

      setFiles(prev => {
        const combined = config.multiple ? [...prev, ...fileStates] : fileStates

        // Enforce max files limit
        if (config.maxFiles && combined.length > config.maxFiles) {
          return combined.slice(0, config.maxFiles)
        }

        return combined
      })

      // Call external handler
      onFilesAdded?.(validFiles)

      // Update external value
      if (onChange) {
        const allFiles = config.multiple ? [...value, ...validFiles] : validFiles
        onChange(allFiles)
      }

      // Auto-upload if enabled
      if (config.autoUpload && (uploadHook || onUpload)) {
        handleUpload(fileStates)
      }
    }
  }, [
    validateFilesInternal,
    config,
    value,
    onChange,
    onFilesAdded,
    onValidationError,
    uploadHook,
    onUpload
  ])

  // Handle file removal
  const handleFileRemoved = useCallback((file: FileWithUIState, index: number) => {
    // Cleanup preview URL
    if (file.preview) {
      cleanupPreviewUrl(file.preview)
    }

    setFiles(prev => prev.filter((_, i) => i !== index))

    // Update external value
    if (onChange) {
      const originalFile = file as File
      const newValue = value.filter(f => f !== originalFile)
      onChange(newValue)
    }

    onFileRemoved?.(file, index)
  }, [value, onChange, onFileRemoved])

  // Handle upload
  const handleUpload = useCallback(async (filesToUpload?: FileWithUIState[]) => {
    const targetFiles = filesToUpload || files.filter(f => f.status === 'pending')

    if (targetFiles.length === 0) return

    setIsUploading(true)
    onUploadStart?.(targetFiles)

    // Create abort controller
    uploadAbortControllerRef.current = new AbortController()

    try {
      // Update file statuses to uploading
      setFiles(prev => prev.map(file =>
        targetFiles.some(tf => tf.id === file.id)
          ? { ...file, status: 'uploading' as const, progress: 0 }
          : file
      ))

      let results: UploadResult[]

      if (batchUploadHook && targetFiles.length > 1) {
        // Use batch upload hook
        results = await batchUploadHook.uploadBatch(targetFiles, {
          onBatchProgress: (completed, total, partialResults) => {
            // Update progress for completed files
            setFiles(prev => prev.map(file => {
              const result = partialResults.find(r => r.file === file)
              if (result) {
                return {
                  ...file,
                  status: result.success ? 'completed' : 'error' as const,
                  progress: result.success ? 100 : 0,
                  error: result.success ? undefined : result.error,
                  result: result.success ? result : undefined
                }
              }
              return file
            }))

            onUploadProgress?.({
              percentage: (completed / total) * 100,
              loaded: completed,
              total: total
            })
          }
        })
      } else if (uploadHook) {
        // Use single upload hook for each file
        results = await Promise.all(
          targetFiles.map(async (file) => {
            try {
              const result = await uploadHook.upload(file, {
                onProgress: (progress: any) => {
                  setFiles(prev => prev.map(f =>
                    f.id === file.id
                      ? { ...f, progress: progress.percentage }
                      : f
                  ))
                  onUploadProgress?.(progress, file)
                }
              })

              // Update file status
              setFiles(prev => prev.map(f =>
                f.id === file.id
                  ? {
                      ...f,
                      status: result.success ? 'completed' : 'error' as const,
                      progress: result.success ? 100 : 0,
                      error: result.success ? undefined : result.error,
                      result: result.success ? result : undefined
                    }
                  : f
              ))

              return result
            } catch (error) {
              const errorResult: UploadResult = {
                success: false,
                file,
                error: error instanceof Error ? error.message : 'Upload failed'
              }

              // Update file status
              setFiles(prev => prev.map(f =>
                f.id === file.id
                  ? { ...f, status: 'error' as const, error: errorResult.error }
                  : f
              ))

              return errorResult
            }
          })
        )
      } else if (onUpload) {
        // Use direct upload handler
        results = await onUpload(targetFiles)

        // Update file statuses based on results
        setFiles(prev => prev.map(file => {
          const result = results.find(r => r.file === file)
          if (result) {
            return {
              ...file,
              status: result.success ? 'completed' : 'error' as const,
              progress: result.success ? 100 : 0,
              error: result.success ? undefined : result.error,
              result: result.success ? result : undefined
            }
          }
          return file
        }))
      } else {
        // No upload handler available
        results = targetFiles.map(file => ({
          success: false,
          file,
          error: 'No upload handler configured'
        }))
      }

      setUploadResults(prev => [...prev, ...results])

      const successfulUploads = results.filter(r => r.success)
      const failedUploads = results.filter(r => !r.success)

      if (successfulUploads.length > 0) {
        onUploadComplete?.(successfulUploads)
      }

      if (failedUploads.length > 0) {
        const error = new Error(`${failedUploads.length} uploads failed`)
        onUploadError?.(error)
      }

    } catch (error) {
      console.error('Upload error:', error)

      // Mark all files as error
      setFiles(prev => prev.map(file =>
        targetFiles.some(tf => tf.id === file.id)
          ? { ...file, status: 'error' as const, error: 'Upload failed' }
          : file
      ))

      onUploadError?.(error instanceof Error ? error : new Error('Upload failed'))
    } finally {
      setIsUploading(false)
      uploadAbortControllerRef.current = null
    }
  }, [
    files,
    uploadHook,
    batchUploadHook,
    onUpload,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError
  ])

  // Handle retry
  const handleRetry = useCallback((file: FileWithUIState, index: number) => {
    // Reset file status
    setFiles(prev => prev.map(f =>
      f.id === file.id
        ? { ...f, status: 'pending' as const, error: undefined, progress: 0 }
        : f
    ))

    // Trigger upload for this file
    if (config.autoUpload) {
      handleUpload([file])
    }
  }, [config.autoUpload, handleUpload])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (!disabled) {
      setIsDragging(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (disabled || !config.dragDropEnabled) return

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      handleFilesAdded(droppedFiles)
    }
  }, [disabled, config.dragDropEnabled, handleFilesAdded])

  // File input handler
  const handleFileInputChange = useCallback(() => {
    const input = fileInputRef.current
    if (input?.files) {
      const selectedFiles = Array.from(input.files)
      handleFilesAdded(selectedFiles)

      // Reset input to allow selecting the same files again
      input.value = ''
    }
  }, [handleFilesAdded])

  const triggerFileSelect = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }, [disabled])

  // Create current state for render prop
  const currentState: FileUploadState = {
    files,
    isUploading,
    isDragging,
    progress: uploadHook?.progress || batchUploadHook?.batchProgress ? {
      percentage: batchUploadHook?.batchProgress?.percentage || uploadHook?.progress?.percentage || 0,
      loaded: uploadHook?.progress?.loaded || 0,
      total: uploadHook?.progress?.total || 0
    } : null,
    errors: validationResults,
    results: uploadResults
  }

  // Render with render prop pattern
  if (children) {
    return (
      <div className={cn('file-upload-field', className)}>
        {children(currentState)}
      </div>
    )
  }

  // Default render
  const hasFiles = files.length > 0
  const hasValidationErrors = validationResults.length > 0

  return (
    <div className={cn('space-y-4', className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={config.accept?.join(',')}
        multiple={config.multiple}
        disabled={disabled}
        onChange={handleFileInputChange}
        className="sr-only"
      />

      {/* Validation messages (top) */}
      {hasValidationErrors && config.validationPosition === 'top' && (
        <ValidationMessages
          errors={validationResults}
          position="top"
          customMessages={config.customErrorMessages}
        />
      )}

      {/* Drop zone */}
      <FileDropZone
        accept={config.accept}
        multiple={config.multiple}
        disabled={disabled || (config.maxFiles ? files.length >= config.maxFiles : false)}
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileSelect}
        className={cn(
          config.variant === 'minimal' && 'border border-solid',
          hasFiles && config.variant !== 'minimal' && 'border-dashed border-gray-300 bg-gray-50'
        )}
        ariaLabel={config.ariaLabel}
      >
        {hasFiles && config.variant !== 'minimal' ? (
          // Show files in drop zone
          <div className="p-4">
            <FileList
              files={files}
              showThumbnails={config.showThumbnails}
              thumbnailSize={config.thumbnailSize}
              showFileInfo={config.showFileInfo}
              showProgress={config.showProgress}
              onRemove={handleFileRemoved}
              onRetry={handleRetry}
              variant={config.variant}
            />

            {/* Upload controls */}
            {!config.autoUpload && files.some(f => f.status === 'pending') && (
              <div className="mt-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => handleUpload()}
                  disabled={isUploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? 'Uploading...' : 'Upload Files'}
                </button>
              </div>
            )}
          </div>
        ) : (
          // Empty state
          <FileDropZoneContent
            isDragging={isDragging}
            accept={config.accept}
            multiple={config.multiple}
            maxSize={config.maxSize}
            dragDropText={config.dragDropText}
            browseText={config.browseText}
            emptyStateText={config.emptyStateText}
            disabled={disabled}
          />
        )}
      </FileDropZone>

      {/* File list (external) */}
      {hasFiles && config.variant === 'minimal' && (
        <FileList
          files={files}
          showThumbnails={config.showThumbnails}
          thumbnailSize={config.thumbnailSize}
          showFileInfo={config.showFileInfo}
          showProgress={config.showProgress}
          onRemove={handleFileRemoved}
          onRetry={handleRetry}
          variant={config.variant}
        />
      )}

      {/* Batch progress */}
      {isUploading && batchUploadHook?.batchProgress && (
        <BatchProgress
          completed={batchUploadHook.batchProgress.completed}
          total={batchUploadHook.batchProgress.total}
          showCurrentFile={true}
        />
      )}

      {/* Validation messages (bottom) */}
      {hasValidationErrors && config.validationPosition !== 'top' && (
        <ValidationMessages
          errors={validationResults}
          position={config.validationPosition || 'bottom'}
          customMessages={config.customErrorMessages}
        />
      )}

      {/* Placeholder content */}
      {placeholder && !hasFiles && !hasValidationErrors && (
        <div className="text-center py-4 text-gray-500">
          {placeholder}
        </div>
      )}
    </div>
  )
}